import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { Loading } from 'components';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
	const [accessToken, setAccessToken] = useState(null);
	const [user, setUser] = useState(null);
	const [is_admin, setIsAdmin] = useState(null);
	const [loading, setLoading] = useState(true);
	const accessTokenRef = useRef(null);
	const refreshInFlight = useRef(false);

	useEffect(() => {
		initCsrf();
	}, []);

	useEffect(() => {
		// Guard against React StrictMode double-invoke firing two simultaneous
		// refresh requests — the second would get 401 (token already blacklisted).
		if (refreshInFlight.current) return;
		refreshInFlight.current = true;
		refreshAccessToken().finally(() => {
			refreshInFlight.current = false;
		});
	}, []);

	const initCsrf = () => {
		let url = '/api/csrf/';
		if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
			url = 'http://localhost:8000' + url;
		}
		fetch(url, { credentials: 'include' });
	};

	const getCSRFToken = () => {
		return document.cookie
			.split('; ')
			.find(row => row.startsWith('csrftoken'))
			?.split('=')[1];
	};

	const login = async (username, password) => {
		// Intentionally does not toggle the global `loading` flag — login should show
		// a local spinner on the submit button (like the CHIME site), not the
		// full-page loading overlay.
		let url = '/api/login/';

		if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
			url = 'http://localhost:8000' + url;
		}

		const response = await fetch(url, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRFToken': getCSRFToken(),
			},
			body: JSON.stringify({ username, password }),
		});
		const data = await response.json();

		if (data.authenticated) {
			setAccessToken(data.access);
			accessTokenRef.current = data.access;
			setUser(data.user || null);
		}

		return data;
	};

	const refreshAccessToken = async () => {
		setLoading(true);

		let url = '/api/refresh/';
		if (window.location.hostname === 'localhost') {
			url = 'http://localhost:8000' + url;
		}

		const response = await fetch(url, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'X-CSRFToken': getCSRFToken(),
			},
		});

		if (!response.ok) {
			setLoading(false);
			return null;
		}

		const data = await response.json();

		if (data.access) {
			setAccessToken(data.access);
			accessTokenRef.current = data.access;
			setUser(data.user || null);
			setLoading(false);
			return data.access;
		}
		setLoading(false);
		return null;
	};

	const authFetch = async (url, options = {}) => {
		const csrfToken = getCSRFToken();
		const token = accessToken ? accessToken : accessTokenRef.current;
		const headers = {
			...(options.headers || {}),
			...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
			...(token ? { Authorization: `Bearer ${token}` } : {}),
		};

		if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
			url = 'http://localhost:8000' + url;
		}

		let response = await fetch(url, {
			...options,
			headers,
			credentials: 'include',
		});

		if (response.status === 401) {
			console.log('Access expired. Refreshing...');

			const newAccess = await refreshAccessToken();
			if (!newAccess) {
				console.log('Refresh failed → logging out.');
				logout();
				return response; // return original 401
			}

			const retryHeaders = {
				...(options.headers || {}),
				'X-CSRFToken': csrfToken,
				Authorization: `Bearer ${newAccess}`,
			};

			response = await fetch(url, {
				...options,
				headers: retryHeaders,
				credentials: 'include',
			});
		}

		return response;
	};

	const refreshUser = async () => {
		try {
			const response = await authFetch('/api/me/');
			if (!response.ok) return null;
			const data = await response.json();
			if (data.user) {
				setUser(data.user);
				return data.user;
			}
		} catch {
			// network error — keep existing user state
		}
		return null;
	};

	const logout = async () => {
		setLoading(true);

		let url = '/api/logout/';
		if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
			url = 'http://localhost:8000' + url;
		}
		let response = await fetch(url, {
			method: 'POST',
			credentials: 'include',
			headers: { 'X-CSRFToken': getCSRFToken() },
		});
		let data = await response.json();

		if (data.success) {
			setAccessToken(null);
			accessTokenRef.current = null;
			setUser(null);
		}

		setLoading(false);
		window.location.href = '/';
	};

	return (
		<AuthContext.Provider
			value={{
				accessToken,
				setAccessToken,
				user,
				setUser,
				login,
				logout,
				authFetch,
				refreshUser,
				loading,
				setLoading,
			}}
		>
			<Loading loading={loading} />
			{children}
		</AuthContext.Provider>
	);
};
